import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLE_LABELS, DEFAULT_ROLE_MATRIX, type StaffRole, type UserOutput } from '@1financial/shared';
import { useAuth, getAccessToken, authenticatedFetch as fetch } from '../../lib/auth-context.js';
import { httpError } from '../../lib/http-error.js';
import { useEscape } from '../../hooks/useEscape.js';
import { clsx } from 'clsx';

interface CapabilityRow {
  key: string;
  label: string;
  description: string;
  group: string;
}
interface OverrideRow {
  role: string;
  capability: string;
  allowed: boolean;
  updatedById: string | null;
  updatedAt: string;
}
interface PermissionsData {
  capabilities: CapabilityRow[];
  groups: { key: string; label: string }[];
  roles: StaffRole[];
  defaults: Record<string, Record<string, boolean>>;
  overrides: OverrideRow[];
  matrix: Record<string, Record<string, boolean>>;
}

type Matrix = Record<string, Record<string, boolean>>;

export function AccessControlPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [matrix, setMatrix] = useState<Matrix | null>(null);
  const [overrides, setOverrides] = useState<OverrideRow[]>([]);
  const [defaults, setDefaults] = useState<Matrix>({});
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [tab, setTab] = useState<'matrix' | 'users'>('matrix');

  const query = useQuery({
    queryKey: ['admin-permissions'],
    queryFn: async () => {
      const res = await fetch('/v1/admin/permissions', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: PermissionsData };
      return body.data;
    },
  });

  useEffect(() => {
    if (query.data) {
      setMatrix(query.data.matrix);
      setOverrides(query.data.overrides);
      setDefaults(query.data.defaults);
    }
  }, [query.data]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const overrideMap = useMemo(() => {
    const m = new Map<string, OverrideRow>();
    for (const o of overrides) m.set(`${o.role}:${o.capability}`, o);
    return m;
  }, [overrides]);

  async function toggle(role: string, capability: string) {
    if (!matrix) return;
    const current = matrix[role]?.[capability] ?? false;
    const next = !current;
    const cellKey = `${role}:${capability}`;

    setMatrix((m) => (m ? { ...m, [role]: { ...m[role], [capability]: next } } : m));
    setPending((p) => new Set(p).add(cellKey));
    try {
      const res = await fetch('/v1/admin/permissions', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({ role, capability, allowed: next }),
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { override: OverrideRow } };
      setOverrides((list) => {
        const rest = list.filter((o) => !(o.role === role && o.capability === capability));
        return [...rest, body.data.override];
      });
      // The caller's own effective set may have changed — refresh nav gating etc.
      void queryClient.invalidateQueries({ queryKey: ['my-permissions'] });
      setToast({ kind: 'ok', text: `${next ? 'Granted' : 'Revoked'} ${capability} for ${ROLE_LABELS[role as StaffRole] ?? role}` });
    } catch (err) {
      setMatrix((m) => (m ? { ...m, [role]: { ...m[role], [capability]: current } } : m));
      setToast({ kind: 'error', text: err instanceof Error ? err.message : 'Update failed — reverted' });
    } finally {
      setPending((p) => {
        const copy = new Set(p);
        copy.delete(cellKey);
        return copy;
      });
    }
  }

  /** Guardrails mirrored from the API — the API re-checks everything. */
  function disabledReason(role: string, capability: string, checked: boolean): string | null {
    if (role === 'CEO' && user?.role !== 'CEO') return 'Only a CEO can modify CEO permissions';
    if (capability === 'admin.access' && role === 'CEO' && checked) return 'admin.access cannot be revoked from CEO';
    if (capability === 'admin.access' && role === user?.role && checked) return 'You cannot revoke your own admin.access';
    return null;
  }

  if (query.isLoading || !matrix) {
    return <div className="bg-bg-panel border border-border rounded-xl p-8 text-center text-muted text-sm">Loading permissions…</div>;
  }
  if (query.error) {
    return <div className="bg-bg-panel border border-border rounded-xl p-8 text-center text-rose-400 text-sm">{(query.error as Error).message}</div>;
  }

  const data = query.data!;
  const needle = search.trim().toLowerCase();
  const visibleGroups = data.groups
    .map((g) => ({
      ...g,
      caps: data.capabilities.filter(
        (c) =>
          c.group === g.key &&
          (!needle ||
            c.key.toLowerCase().includes(needle) ||
            c.label.toLowerCase().includes(needle) ||
            c.description.toLowerCase().includes(needle)),
      ),
    }))
    .filter((g) => g.caps.length > 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1 border-b border-border">
        {(['matrix', 'users'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={clsx(
              'px-3 py-2 text-sm border-b-2 -mb-px',
              tab === t ? 'border-accent text-white' : 'border-transparent text-muted hover:text-white',
            )}
          >
            {t === 'matrix' ? 'Role matrix' : 'Users'}
          </button>
        ))}
      </div>

      {tab === 'users' ? (
        <UsersAccessPanel capabilities={data.capabilities} groups={data.groups} roles={data.roles} />
      ) : (
      <>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-muted">
          {data.capabilities.length} capabilities × {data.roles.length} roles · dotted cells are overrides of the built-in defaults
        </p>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter capabilities…"
          className="bg-bg-panel border border-border rounded-lg px-3 py-1.5 text-sm text-white placeholder-muted focus:outline-none focus:border-accent w-full sm:w-64"
        />
      </div>

      <div className="bg-bg-panel border border-border rounded-xl overflow-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="sticky top-0 z-10">
            <tr className="bg-bg-card border-b border-border">
              <th className="px-4 py-3 text-left text-xs font-medium text-muted sticky left-0 bg-bg-card">Capability</th>
              {data.roles.map((role) => (
                <th key={role} className="px-3 py-3 text-center text-xs font-medium text-muted whitespace-nowrap">
                  {ROLE_LABELS[role] ?? role}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleGroups.map((group) => (
              <GroupSection
                key={group.key}
                group={group}
                roles={data.roles}
                matrix={matrix}
                defaults={defaults}
                overrideMap={overrideMap}
                pending={pending}
                disabledReason={disabledReason}
                onToggle={toggle}
              />
            ))}
            {visibleGroups.length === 0 && (
              <tr><td colSpan={data.roles.length + 1} className="px-4 py-8 text-center text-muted">No capabilities match “{search}”.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      </>
      )}

      {toast && (
        <div
          className={clsx(
            'fixed bottom-4 right-4 z-50 px-4 py-2.5 rounded-lg border text-sm shadow-2xl',
            toast.kind === 'ok'
              ? 'bg-bg-panel border-accent text-emerald-300'
              : 'bg-bg-panel border-rose-800 text-rose-300',
          )}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}

function GroupSection(props: {
  group: { key: string; label: string; caps: CapabilityRow[] };
  roles: StaffRole[];
  matrix: Matrix;
  defaults: Matrix;
  overrideMap: Map<string, OverrideRow>;
  pending: Set<string>;
  disabledReason: (role: string, capability: string, checked: boolean) => string | null;
  onToggle: (role: string, capability: string) => void;
}) {
  const { group, roles, matrix, defaults, overrideMap, pending, disabledReason, onToggle } = props;
  return (
    <>
      <tr className="bg-bg-card/60 border-b border-border">
        <td colSpan={roles.length + 1} className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted sticky left-0">
          {group.label}
        </td>
      </tr>
      {group.caps.map((cap) => {
        const edited = roles.some((r) => overrideMap.has(`${r}:${cap.key}`));
        return (
          <tr key={cap.key} className="border-b border-border/50 hover:bg-bg-hover/40">
            <td className="px-4 py-2.5 sticky left-0 bg-bg-panel">
              <div className="flex items-center gap-2">
                <span className="text-white">{cap.label}</span>
                {edited && (
                  <span className="text-[10px] uppercase tracking-wider text-accent border border-accent/50 rounded px-1 py-px">custom</span>
                )}
              </div>
              <div className="text-[11px] text-muted">{cap.key} — {cap.description}</div>
            </td>
            {roles.map((role) => {
              const checked = matrix[role]?.[cap.key] ?? false;
              const override = overrideMap.get(`${role}:${cap.key}`);
              const reason = disabledReason(role, cap.key, checked);
              const busy = pending.has(`${role}:${cap.key}`);
              const isDefault = (defaults[role]?.[cap.key] ?? false) === checked && !override;
              const title = reason ?? (override ? `Override — default is ${(defaults[role]?.[cap.key] ?? false) ? 'allowed' : 'denied'}` : undefined);
              return (
                <td key={role} className="px-3 py-2.5 text-center">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={reason !== null || busy}
                    onChange={() => onToggle(role, cap.key)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (reason === null && !busy) onToggle(role, cap.key);
                      }
                    }}
                    title={title}
                    className={clsx(
                      'w-4 h-4 accent-emerald-500',
                      reason !== null || busy ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
                      !isDefault && 'outline outline-1 outline-dotted outline-accent rounded-sm',
                    )}
                  />
                </td>
              );
            })}
          </tr>
        );
      })}
    </>
  );
}


interface UserOverrideItem {
  userId: string;
  capability: string;
  allowed: boolean;
}

function UsersAccessPanel({ capabilities, groups, roles }: {
  capabilities: CapabilityRow[];
  groups: { key: string; label: string }[];
  roles: StaffRole[];
}) {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const users = useQuery({
    queryKey: ['admin-users-list'],
    queryFn: async () => {
      const res = await fetch('/v1/users?limit=100', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: UserOutput[] } };
      return body.data.items.filter((u) => u.status !== 'DISABLED');
    },
  });

  const overrides = useQuery({
    queryKey: ['admin-user-overrides'],
    queryFn: async () => {
      const res = await fetch('/v1/admin/user-overrides', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: UserOverrideItem[] } };
      return body.data.items;
    },
  });

  const customCount = useMemo(() => {
    const m = new Map<string, number>();
    for (const o of overrides.data ?? []) m.set(o.userId, (m.get(o.userId) ?? 0) + 1);
    return m;
  }, [overrides.data]);

  const needle = search.trim().toLowerCase();
  const visible = (users.data ?? []).filter(
    (u) => !needle || u.displayName.toLowerCase().includes(needle) || u.email.toLowerCase().includes(needle),
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-muted">
          {visible.length} staff · click a user to grant or revoke individual capabilities — user overrides win over the role matrix
        </p>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter users…"
          className="bg-bg-panel border border-border rounded-lg px-3 py-1.5 text-sm text-white placeholder-muted focus:outline-none focus:border-accent w-full sm:w-64"
        />
      </div>

      <div className="bg-bg-panel border border-border rounded-xl overflow-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="bg-bg-card border-b border-border text-left">
              <th className="px-4 py-3 text-xs font-medium text-muted">Name</th>
              <th className="px-4 py-3 text-xs font-medium text-muted">Email</th>
              <th className="px-4 py-3 text-xs font-medium text-muted">Role</th>
              <th className="px-4 py-3 text-xs font-medium text-muted">Overrides</th>
            </tr>
          </thead>
          <tbody>
            {users.isLoading && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-muted">Loading users…</td></tr>
            )}
            {users.error && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-rose-400">{(users.error as Error).message}</td></tr>
            )}
            {visible.map((u) => {
              const n = customCount.get(u.id) ?? 0;
              return (
                <tr
                  key={u.id}
                  onClick={() => setSelectedId(u.id)}
                  className="border-b border-border/50 hover:bg-bg-hover/40 cursor-pointer"
                >
                  <td className="px-4 py-2.5 text-white">{u.displayName}</td>
                  <td className="px-4 py-2.5 text-muted">{u.email}</td>
                  <td className="px-4 py-2.5 text-muted">{ROLE_LABELS[u.role as StaffRole] ?? u.role}</td>
                  <td className="px-4 py-2.5">
                    {n > 0 ? (
                      <span className="text-[10px] uppercase tracking-wider text-accent border border-accent/50 rounded px-1 py-px">
                        custom · {n}
                      </span>
                    ) : (
                      <span className="text-xs text-muted">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {!users.isLoading && !users.error && visible.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-muted">No users match “{search}”.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedId && (
        <UserPermissionsDrawer
          userId={selectedId}
          capabilities={capabilities}
          groups={groups}
          roles={roles}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

type UserPermState = 'granted' | 'revoked' | 'inherit';
interface UserPermItem {
  capability: string;
  state: UserPermState;
  roleEffective: boolean;
  effective: boolean;
}
interface UserPermsData {
  user: { id: string; email: string; displayName: string; role: string; status: string };
  items: UserPermItem[];
}

function UserPermissionsDrawer({ userId, capabilities, groups, roles, onClose }: {
  userId: string;
  capabilities: CapabilityRow[];
  groups: { key: string; label: string }[];
  roles: StaffRole[];
  onClose: () => void;
}) {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [items, setItems] = useState<UserPermItem[] | null>(null);
  const [pending, setPending] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const query = useQuery({
    queryKey: ['admin-user-permissions', userId],
    queryFn: async () => {
      const res = await fetch(`/v1/admin/users/${userId}/permissions`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: UserPermsData };
      return body.data;
    },
  });

  useEffect(() => {
    if (query.data) setItems(query.data.items);
  }, [query.data]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  useEscape(onClose);

  const target = query.data?.user;
  const roleLocked = target?.role === 'CEO' && currentUser?.role !== 'CEO';

  async function setState(capability: string, state: 'grant' | 'revoke' | 'inherit') {
    if (!items) return;
    const prev = items;
    const item = items.find((i) => i.capability === capability);
    if (!item) return;
    const nextState: UserPermState = state === 'grant' ? 'granted' : state === 'revoke' ? 'revoked' : 'inherit';
    const nextEffective = state === 'grant' ? true : state === 'revoke' ? false : item.roleEffective;
    setItems(items.map((i) => (i.capability === capability ? { ...i, state: nextState, effective: nextEffective } : i)));
    setPending((p) => new Set(p).add(capability));
    try {
      const res = await fetch(`/v1/admin/users/${userId}/permissions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: JSON.stringify({ capability, state }),
      });
      if (!res.ok) throw await httpError(res);
      void queryClient.invalidateQueries({ queryKey: ['my-permissions'] });
      void queryClient.invalidateQueries({ queryKey: ['admin-user-overrides'] });
      setToast({ kind: 'ok', text: `${capability} → ${state}` });
    } catch (err) {
      setItems(prev);
      setToast({ kind: 'error', text: err instanceof Error ? err.message : 'Update failed — reverted' });
    } finally {
      setPending((p) => {
        const copy = new Set(p);
        copy.delete(capability);
        return copy;
      });
    }
  }

  const capMeta = new Map(capabilities.map((c) => [c.key, c]));
  const byGroup = groups
    .map((g) => ({ ...g, caps: (items ?? []).filter((i) => capMeta.get(i.capability)?.group === g.key) }))
    .filter((g) => g.caps.length > 0);

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="absolute inset-y-0 right-0 w-full max-w-md bg-bg-panel border-l border-border shadow-2xl flex flex-col">
        <div className="flex items-start justify-between px-5 py-4 border-b border-border">
          <div>
            <h2 className="text-base font-semibold text-white">{target?.displayName ?? '…'}</h2>
            <p className="text-xs text-muted">
              {target?.email} · {ROLE_LABELS[target?.role as StaffRole] ?? target?.role ?? ''}
            </p>
            {roleLocked && (
              <p className="text-xs text-amber-300 mt-1">Only a CEO can modify a CEO's permissions.</p>
            )}
          </div>
          <button onClick={onClose} className="text-muted hover:text-white text-sm">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {query.isLoading && <p className="text-sm text-muted">Loading…</p>}
          {query.error && <p className="text-sm text-rose-400">{(query.error as Error).message}</p>}
          {items && target && (
            <AccessSummary items={items} role={target.role} capabilities={capabilities} groups={groups} roles={roles} />
          )}
          {byGroup.map((g) => (
            <div key={g.key}>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted mb-1.5">{g.label}</p>
              <div className="space-y-1">
                {g.caps.map((item) => {
                  const meta = capMeta.get(item.capability);
                  const selfAdminLock = item.capability === 'admin.access' && target?.id === currentUser?.id;
                  const disabled = roleLocked || selfAdminLock || pending.has(item.capability);
                  const lockTitle = selfAdminLock
                    ? 'You cannot modify your own admin.access'
                    : roleLocked
                      ? "Only a CEO can modify a CEO's permissions"
                      : undefined;
                  return (
                    <div
                      key={item.capability}
                      className={clsx(
                        'flex items-center justify-between gap-2 rounded-lg border px-3 py-2',
                        item.state === 'inherit' ? 'border-border/50' : 'border-accent/50 bg-accent/5',
                      )}
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-white truncate">{meta?.label ?? item.capability}</span>
                          {item.state === 'granted' && (
                            <span className="text-[10px] uppercase tracking-wider text-emerald-300 border border-emerald-700 rounded px-1 py-px shrink-0">granted</span>
                          )}
                          {item.state === 'revoked' && (
                            <span className="text-[10px] uppercase tracking-wider text-rose-300 border border-rose-700 rounded px-1 py-px shrink-0">revoked</span>
                          )}
                          {item.state === 'inherit' && (
                            <span className="text-[10px] uppercase tracking-wider text-muted border border-border rounded px-1 py-px shrink-0">
                              {item.roleEffective ? 'inherited · allowed' : 'inherited · denied'}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-muted truncate">{item.capability}</p>
                      </div>
                      <div className="flex shrink-0 rounded-lg border border-border overflow-hidden text-xs">
                        {(['grant', 'revoke', 'inherit'] as const).map((s) => {
                          const active =
                            (s === 'grant' && item.state === 'granted') ||
                            (s === 'revoke' && item.state === 'revoked') ||
                            (s === 'inherit' && item.state === 'inherit');
                          return (
                            <button
                              key={s}
                              disabled={disabled}
                              onClick={() => setState(item.capability, s)}
                              title={lockTitle}
                              className={clsx(
                                'px-2 py-1 capitalize',
                                active && s === 'grant' && 'pill-success',
                                active && s === 'revoke' && 'pill-danger',
                                active && s === 'inherit' && 'bg-bg-hover text-white',
                                !active && 'text-muted hover:text-white',
                                disabled && 'opacity-40 cursor-not-allowed',
                              )}
                            >
                              {s}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {toast && (
        <div
          className={clsx(
            'absolute bottom-4 right-4 z-50 px-4 py-2.5 rounded-lg border text-sm shadow-2xl',
            toast.kind === 'ok'
              ? 'bg-bg-panel border-accent text-emerald-300'
              : 'bg-bg-panel border-rose-800 text-rose-300',
          )}
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}


/**
 * Row-level data scope per role — phrasing mirrors apps/api/src/lib/scope.ts.
 * Static on purpose: scoping is role-based, not capability-based.
 */
const SCOPE_BY_ROLE: Record<string, string> = {
  CEO: 'Full scope — sees all clients, engagements and compliance items.',
  GM: 'Full scope — sees all clients, engagements and compliance items.',
  Manager: 'Full scope — sees all clients, engagements and compliance items.',
  TeamLead: 'Own + managed scope — clients they own or linked to their engagements; compliance items they manage or are assigned to.',
  Staff: 'Assignment scope — clients they own or linked to their engagements; compliance items directly assigned to them.',
  CLIENT: 'Client portal only — no internal data.',
};

/** Plain-English "what can this user do" panel + role what-if comparison. */
function AccessSummary({ items, role, capabilities, groups, roles }: {
  items: UserPermItem[];
  role: string;
  capabilities: CapabilityRow[];
  groups: { key: string; label: string }[];
  roles: StaffRole[];
}) {
  const [whatIf, setWhatIf] = useState('');
  const capMeta = new Map(capabilities.map((c) => [c.key, c]));

  const manages: string[] = [];
  const views: string[] = [];
  const none: string[] = [];
  for (const g of groups) {
    const gcaps = items.filter((i) => capMeta.get(i.capability)?.group === g.key);
    if (gcaps.some((i) => i.effective && !i.capability.endsWith('.view'))) manages.push(g.label);
    else if (gcaps.some((i) => i.effective)) views.push(g.label);
    else none.push(g.label);
  }

  const diff = useMemo(() => {
    if (!whatIf) return null;
    const matrix = DEFAULT_ROLE_MATRIX[whatIf as StaffRole] ?? {};
    return {
      gains: items.filter((i) => !i.effective && matrix[i.capability] === true),
      losses: items.filter((i) => i.effective && matrix[i.capability] !== true),
    };
  }, [whatIf, items]);

  return (
    <div className="rounded-lg border border-border bg-bg-card/60 p-3 space-y-2">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted">What can this user do?</p>
      <div className="text-xs space-y-1">
        {manages.length > 0 && (
          <p><span className="text-emerald-300">Manages:</span> <span className="text-white">{manages.join(', ')}</span></p>
        )}
        {views.length > 0 && (
          <p><span className="text-accent">Views only:</span> <span className="text-white">{views.join(', ')}</span></p>
        )}
        {none.length > 0 && (
          <p><span className="text-rose-300">No access:</span> <span className="text-muted">{none.join(', ')}</span></p>
        )}
        <p><span className="text-muted">Data scope:</span> <span className="text-white">{SCOPE_BY_ROLE[role] ?? '—'}</span></p>
      </div>

      <div className="pt-2 border-t border-border/50">
        <label className="block text-[11px] text-muted mb-1">If role were…</label>
        <select
          value={whatIf}
          onChange={(e) => setWhatIf(e.target.value)}
          className="w-full bg-bg-base border border-border rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none focus:border-accent"
        >
          <option value="">— compare with a role —</option>
          {roles.filter((r) => r !== role).map((r) => (
            <option key={r} value={r}>{ROLE_LABELS[r] ?? r}</option>
          ))}
        </select>
        {diff && (
          <div className="mt-2 space-y-1 text-xs">
            <p className="text-muted">
              vs. built-in defaults for {ROLE_LABELS[whatIf as StaffRole] ?? whatIf} (role-level custom overrides not included):
            </p>
            {diff.gains.length > 0 && (
              <p>
                <span className="text-emerald-300">Gains:</span>{' '}
                <span className="text-white">{diff.gains.map((i) => capMeta.get(i.capability)?.label ?? i.capability).join(', ')}</span>
              </p>
            )}
            {diff.losses.length > 0 && (
              <p>
                <span className="text-rose-300">Loses:</span>{' '}
                <span className="text-white">{diff.losses.map((i) => capMeta.get(i.capability)?.label ?? i.capability).join(', ')}</span>
              </p>
            )}
            {diff.gains.length === 0 && diff.losses.length === 0 && (
              <p className="text-muted">No difference from the current effective set.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
